import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { jsonApi } from '@/lib/jsonApi';
import { DetailNav } from '@/components/responseDetail/DetailHeader';
import DetailBody from '@/components/responseDetail/DetailBody';

export default function ResponseDetail() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasGapLibrary } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => jsonApi(`/responses/${encodeURIComponent(responseId)}/detail`), [responseId]);
  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    load()
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e); });
    return () => { live = false; };
  }, [load]);
  const reload = () => load().then(setData).catch(setError);

  const back = () => (location.state?.fromList ? navigate(-1) : navigate('/responses'));
  const go = (id) => { navigate(`/responses/${encodeURIComponent(id)}`, { replace: true, state: location.state }); window.scrollTo(0, 0); };

  return (
    <DashboardLayout>
      <div className="max-w-[1400px] mx-auto space-y-5" data-testid="response-detail-page">
        <DetailNav onBack={back} prevId={data?.navigation?.prev_id} nextId={data?.navigation?.next_id} onGo={go} />
        {!data && !error && (
          <div className="py-24 text-center" data-testid="response-detail-loading"><div className="loading-spinner mx-auto mb-4" /><p className="text-[#64748B]">Loading response…</p></div>
        )}
        {error && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl py-16 px-6 text-center" data-testid="response-detail-error">
            <SearchX className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">{error.status === 404 ? 'We couldn’t find this response' : 'This response could not be loaded'}</p>
            <p className="text-sm text-[#64748B] mt-1">{error.status === 404 ? 'It may belong to a branch you don’t have access to.' : error.message}</p>
            <Button onClick={() => navigate('/responses')} className="mt-5 bg-[#1A3E6F] hover:bg-[#15325A]" data-testid="response-detail-error-back">Back to Responses</Button>
          </div>
        )}
        {data && <DetailBody key={data.response.response_id} data={data} isPlatformAdmin={user?.role === 'platform_admin'} hasGapLibrary={hasGapLibrary} onReload={reload} />}
      </div>
    </DashboardLayout>
  );
}
